<template>
    <div>
        <div class="form-group mb-4">
            <label v-if="!removeTitle">
                <strong>
                    Estimated Route
                </strong>
            </label>
            <div class="input-group">
                <img :src="routeImagePath" style="width: 100%" alt="Image of drivers route between collection and delivery locations" />
            </div>
        </div>
        <div v-if="mileage">
            <div class="row">
                <div class="col-lg-6">
                    <label for="mileage">
                        <strong>
                            Estimated Mileage
                        </strong>
                    </label>
                    <div class="input-group">
                        <span class="input-group-text triple">
                            <img
                                src='https://maps.google.com/mapfiles/ms/icons/green-dot.png'
                                alt="Green dot icon indicating collection location"
                                class="map-icon"
                            />
                            <span class="map-arrow"><--></span>
                            <img
                                src='https://maps.google.com/mapfiles/ms/icons/red-dot.png'
                                alt="Red dot icon indicating delivery location"
                                class="map-icon"
                            />
                        </span>
                        <!-- The current mileage from the job -->
                        <input class="form-control" placeholder="(awaiting route)" readonly="readonly" name="mileage" v-model="internalMileage" type="text" id="mileage">
                    </div>
                </div>
                <div class="col-lg-6">
                    <label for="current-mileage">
                        <strong>
                            Current Estimated Mileage
                        </strong>
                    </label>
                    <div class="input-group">
                        <span class="input-group-text triple">
                            <img
                                src='https://maps.google.com/mapfiles/ms/icons/green-dot.png'
                                alt="Green dot icon indicating collection location"
                                class="map-icon"
                            />
                            <span class="map-arrow"><--></span>
                            <img
                                src='https://maps.google.com/mapfiles/ms/icons/red-dot.png'
                                alt="Red dot icon indicating delivery location"
                                class="map-icon"
                            />
                        </span>
                        <!-- Calculated mileage from the new route -->
                        <input class="form-control" placeholder="(awaiting route)" readonly="readonly" name="current-mileage" v-model="newInternalMileage" type="text" id="current-mileage">
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

    .map-icon {
        height: 14px;
    }

    .map-arrow {
        font-size: 8px;
    }

</style>


<script>

import { Loader } from "@googlemaps/js-api-loader";
import Swal from "sweetalert2";

const APIKEY = 'AIzaSyD_tfgEUWxTtVczG5euJnkbeZ9DryHp4F4';

export default {

    props: {
        mileage: {
            type: Number,
            required: false,
        },
        removeTitle: {
            type: Boolean,
            required: false,
            default: false,
        },
        movements: {
            type: Array,
            required: false,
            default: () => []
        },
        selectedMovement: {
            type: Object,
            required: false,
            default: null,
        }
    },

    data() {
        return {
            routeImagePath: '/images/movements/awaiting-route-placeholder.png',
            internalMileage: this.mileage,
            newInternalMileage: this.mileage,
            isRunning: false,
        }
    },

    methods: {

        async loadLibraries() {
            const core = await google.maps.importLibrary("core");
            const routes = await google.maps.importLibrary("routes");

            return {
                LatLng: core.LatLng,
                DirectionsService: routes.DirectionsService,
                DirectionsStatus: routes.DirectionsStatus,
                TravelMode: routes.TravelMode,
                UnitSystem: core.UnitSystem,
            };
        },

        async estimateMileage() {

            if ( this.isRunning ) {
                return;
            }

            if (!this.movements || this.movements.length === 0) {
                console.warn("No movement data – cannot build route");
                return;
            }

            this.isRunning = true;

            const { LatLng, DirectionsService, DirectionsStatus, TravelMode, UnitSystem } = await this.loadLibraries();
            const directionsService = new DirectionsService();

            for (let i = 0; i < this.movements.length; i++) {
                const movement = this.movements[i];
                if ( !movement.collection_lat || !movement.collection_lng || !movement.delivery_lat || !movement.delivery_lng ) {
                    console.warn(`Movement ${movement.position} point is missing collection or delivery coordinates`);
                    this.isRunning = false;
                    return;
                }
            }

            const origin = new LatLng(
                this.movements[0].collection_lat,
                this.movements[0].collection_lng
            );

            const destination = new LatLng(
                this.movements[this.movements.length - 1].delivery_lat,
                this.movements[this.movements.length - 1].delivery_lng
            );

            const waypoints = this.movements.length > 1
                ? this.movements.slice(0, -1).map(m => ({
                    location: new LatLng(m.delivery_lat, m.delivery_lng),
                    stopover: true
                }))
                : [];

            let routeOptions = {
                origin: origin,
                destination: destination,
                waypoints: waypoints,
                travelMode: TravelMode.DRIVING,
                unitSystem: UnitSystem.IMPERIAL
            };

            directionsService.route(routeOptions, (result, status) => {
                if (status === DirectionsStatus.OK) {

                    let sizeWidth = '1000';
                    let sizeHeight = '450';

                    let routeLine = result.routes[0].overview_polyline;

                    let markers = [
                        `markers=color:green%7Clabel:C%7C${this.movements[0].collection_lat},${this.movements[0].collection_lng}`
                    ];

                    if (this.movements.length > 1) {
                        const lastLat = this.movements[this.movements.length - 1].delivery_lat;
                        const lastLng = this.movements[this.movements.length - 1].delivery_lng;

                        for (let i = 0; i < this.movements.length - 1; i++) {
                            const m = this.movements[i];
                            if (m.delivery_lat !== lastLat || m.delivery_lng !== lastLng) {
                                markers.push(`markers=color:blue%7C${m.delivery_lat},${m.delivery_lng}`);
                            }
                        }
                    }

                    markers.push(`markers=color:red%7Clabel:D%7C${this.movements[this.movements.length - 1].delivery_lat},${this.movements[this.movements.length - 1].delivery_lng}`);

                    let markersString = markers.join('&');

                    this.routeImagePath = `https://maps.googleapis.com/maps/api/staticmap?size=${sizeWidth}x${sizeHeight}&${markersString}&path=color:0x1AB394FF%7Cenc:${routeLine}&key=${APIKEY}`;

                    let totalMileage = 0;
                    let routeLegDistance = [];
                    result.routes[0].legs.forEach(leg => {
                        let legDistance = leg.distance.text.split(" ");
                        if (legDistance[1] === "ft") {
                            totalMileage += parseFloat(legDistance[0]) / 5280;
                        } else if (legDistance[1] === "mi") {
                            totalMileage += parseFloat(legDistance[0]);
                            routeLegDistance.push({
                                distance: parseFloat(legDistance[0]),
                                unit: legDistance[1]
                            });
                        }
                    });

                    // Account for minor route changes from the Google API
                    if (this.internalMileage < ( totalMileage - 5 ) || this.internalMileage > ( totalMileage + 5 )) {
                        this.newInternalMileage = Math.ceil(totalMileage);
                    }

                    this.$emit('update-mileage', this.newInternalMileage);
                    this.$emit('update-route-leg-distances', routeLegDistance);
                } else {
                    console.error("Failed to estimate mileage");

                    if (status === "ZERO_RESULTS") {

                        Swal.fire({
                            title: 'No route found',
                            text: 'No route could be found between the collection and delivery locations. Please try another destination',
                            icon: 'error',
                            confirmButtonText: 'OK',
                            width: '600px',
                            timer: 5000,
                            timerProgressBar: true
                        });

                    }
                }
                this.isRunning = false;
            });
        },

    },

    async mounted() {

        if (!APIKEY) {
            console.error("Google Maps API key is not set in environment variables");
            return;
        }

        const loader = new Loader({
            apiKey: APIKEY,
            version: "weekly",
            libraries: ["places", "geocoding", "maps", "marker"],
        });

        // This is used literally as a way to detect Google is loaded. We don't actually need to load a library here
        await loader.importLibrary('places');

        await this.estimateMileage();

    },

    watch: {
        movements: {
            handler() {
                this.estimateMileage();
            },
            deep: true
        }
    }

}
</script>
